/**
 * Old paths, sent on to where the screen lives now, with what the link carried.
 *
 * A bookmark, a runbook line or a sentence in a sent email holds the old path,
 * often with a `?tab=` or a filter on it. A bare `<Navigate>` drops all of that
 * and lands somebody on the right screen in the wrong state, which reads as the
 * link being broken. So the query and the hash travel with the redirect, and a
 * key the target names for itself wins over the same key on the old link.
 *
 * Always `replace`, so Back does not return to a path that only bounces.
 */
import { Navigate, useLocation, useParams } from 'react-router-dom'

/**
 * The address an old link lands on.
 *
 * `to` is a path written in this codebase, optionally with its own query.
 * `:name` in it is filled from the route's own params, encoded, so
 * `/inbox?labelId=:labelId` carries the id across. `drop` names keys from the
 * old link that mean something else at the target and must not travel.
 */
export function redirectTarget(
  to: string,
  from: { search: string; hash: string },
  params: Readonly<Record<string, string | undefined>> = {},
  drop: readonly string[] = [],
): string {
  const filled = to.replace(/:([A-Za-z][A-Za-z0-9]*)/g, (_, name: string) => encodeURIComponent(params[name] ?? ''))
  const at = filled.indexOf('?')
  const pathname = at === -1 ? filled : filled.slice(0, at)
  const own = new URLSearchParams(at === -1 ? '' : filled.slice(at + 1))
  const query = new URLSearchParams(from.search)
  for (const key of drop) query.delete(key)
  for (const key of new Set(own.keys())) query.delete(key)
  for (const [key, value] of own) query.append(key, value)
  const search = query.toString()
  return `${pathname}${search === '' ? '' : `?${search}`}${from.hash}`
}

/** A route element that sends the old path on, keeping its query and hash. */
export function Redirect({ to, drop }: { to: string; drop?: readonly string[] }) {
  const location = useLocation()
  const params = useParams()
  return <Navigate to={redirectTarget(to, location, params, drop)} replace />
}
